import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Button, Chip, CircularProgress, Alert } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  PeopleOutline,
  PersonAddAlt1Outlined,
  HowToRegOutlined,
  AdminPanelSettingsOutlined,
  BlockOutlined,
  ChevronRight,
} from '@mui/icons-material';
import { useSupabase } from '../../contexts/SupabaseContext';
import { useAccess } from '../../contexts/AccessContext';
import { MODULES } from '../../config/modules';
import { SummaryTile } from '../utilities/FastagManager';
import { ChartCard, chartSx } from '../../components/analysis/ChartParts';
import { SERIES_COLORS } from '../../utils/transactionAnalysis';
import { adminErrorMessage, moduleOfTable } from './adminShared';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (ym, spans) => {
  const [y, m] = ym.split('-').map(Number);
  return spans ? `${MONTHS[m - 1]} '${String(y).slice(2)}` : MONTHS[m - 1];
};
const number = (n) => new Intl.NumberFormat('en-IN').format(n || 0);

const AdminOverview = ({ onOpenTab }) => {
  const { supabase } = useSupabase();
  const { isEnabled } = useAccess();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    supabase.rpc('admin_overview').then(({ data: result, error: rpcError }) => {
      if (cancelled) return;
      if (rpcError) setError(adminErrorMessage(rpcError));
      else setData(result);
    });
    return () => { cancelled = true; };
  }, [supabase]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  const signups = data.signups_by_month || [];
  const spans = signups.length > 1 && signups[0].month.slice(0, 4) !== signups[signups.length - 1].month.slice(0, 4);
  const perModule = {};
  Object.entries(data.table_counts || {}).forEach(([table, count]) => {
    const key = moduleOfTable(table);
    perModule[key] = (perModule[key] || 0) + Number(count || 0);
  });
  const moduleRows = MODULES
    .filter(m => m.key in perModule)
    .map(m => ({ ...m, records: perModule[m.key] }))
    .sort((a, b) => b.records - a.records);
  const maxRecords = moduleRows[0]?.records || 1;

  return (
    <Box>
      <Paper data-tour="admin-summary" sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: { xs: 1, sm: 1.5 } }}>
        <SummaryTile icon={<PeopleOutline />} color="primary" value={number(data.total_users)} label="Users" />
        <SummaryTile icon={<PersonAddAlt1Outlined />} color="success" value={number(data.new_30d)} label="New in 30 days" />
        <SummaryTile icon={<HowToRegOutlined />} color="info" value={number(data.active_30d)} label="Signed in, 30 days" />
        <SummaryTile icon={<AdminPanelSettingsOutlined />} color="secondary" value={number(data.admins)} label={data.admins === 1 ? 'Admin' : 'Admins'} />
        <SummaryTile icon={<BlockOutlined />} color={data.disabled ? 'error' : 'info'} value={number(data.disabled)} label="Disabled accounts" />
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '7fr 5fr' }, gap: 3, alignItems: 'start' }}>
        <ChartCard data-tour="admin-signups" title="Sign-ups by Month" subtitle="New accounts in each of the last 12 months." style={{ marginBottom: 0 }}>
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <BarChart
              height={280}
              xAxis={[{ scaleType: 'band', data: signups.map(s => monthLabel(s.month, spans)), categoryGapRatio: 0.55 }]}
              yAxis={[{ tickMinStep: 1 }]}
              series={[{ id: 'signups', label: 'Sign-ups', data: signups.map(s => s.count), color: SERIES_COLORS[0] }]}
              grid={{ horizontal: true }}
              borderRadius={4}
              margin={{ top: 12, right: 8, bottom: 28, left: 40 }}
              slotProps={{ legend: { hidden: true } }}
              sx={chartSx}
            />
          </Box>
          <Button size="small" endIcon={<ChevronRight />} onClick={() => onOpenTab('users')}>Manage users</Button>
        </ChartCard>

        <ChartCard data-tour="admin-data" title="Data by Module" subtitle="Records stored across all users." style={{ marginBottom: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {moduleRows.map(m => (
              <Box key={m.key}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{m.name}</Typography>
                  {!isEnabled(m.key) && <Chip label="Off" size="small" sx={{ height: 20, fontSize: '0.6875rem' }} />}
                  <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{number(m.records)}</Typography>
                </Box>
                <Box sx={{ height: 8, borderRadius: '0 4px 4px 0', bgcolor: SERIES_COLORS[0], width: `${Math.max(1, (m.records / maxRecords) * 100)}%` }} />
              </Box>
            ))}
          </Box>
          <Button size="small" sx={{ mt: 1.5 }} endIcon={<ChevronRight />} onClick={() => onOpenTab('modules')}>Manage modules</Button>
        </ChartCard>
      </Box>
    </Box>
  );
};

export default AdminOverview;
